import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { 
    LayoutDashboard, 
    Users, 
    ClipboardList, 
    Settings,
    LogOut,
    Menu,
    ChevronRight,
    UserCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const adminNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/customers', icon: Users, label: 'Customers' },
    { path: '/jobs', icon: ClipboardList, label: 'Jobs' },
    { path: '/settings', icon: Settings, label: 'Settings' },
];

const surveyorNavItems = [
    { path: '/jobs', icon: ClipboardList, label: 'Jobs' },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { path: '/customers', icon: Users, label: 'Customers' },
];

export const Layout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isAdmin } = useAuth();
    const { app_name, logo_base64 } = useAppSettings();
    const displayName = app_name || 'Plaster Survey';

    const AppIcon = ({ size = 'md' }) => {
        const cls = size === 'sm'
            ? 'w-8 h-8 rounded-md'
            : 'w-10 h-10 rounded-lg';
        return logo_base64
            ? <img src={`data:image/png;base64,${logo_base64}`} alt="Logo" className={`${cls} object-contain bg-white p-0.5`} />
            : <div className={`${cls} bg-red-600 flex items-center justify-center`}>
                <span className={`text-white font-bold ${size === 'sm' ? 'text-sm' : 'text-base'}`}>PS</span>
              </div>;
    };
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navItems = isAdmin() ? adminNavItems : surveyorNavItems;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path) => location.pathname.startsWith(path);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Top Header - Mobile */}
            <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
                <Link to={isAdmin() ? '/dashboard' : '/jobs'} className="flex items-center gap-2">
                    <AppIcon size="sm" />
                    <span className="font-heading font-bold text-lg text-slate-900 uppercase tracking-tight">
                        {displayName}
                    </span>
                </Link>
                
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="mobile-menu-btn">
                            <Menu className="h-5 w-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-72 p-0">
                        <div className="flex flex-col h-full">
                            <div className="p-4 border-b border-slate-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                                        <span className="text-white font-bold">
                                            {user?.name?.charAt(0) || 'U'}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">{user?.name}</p>
                                        <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <nav className="flex-1 p-2">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                                            isActive(item.path)
                                                ? 'bg-red-50 text-red-600'
                                                : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                        data-testid={`nav-${item.label.toLowerCase()}`}
                                    >
                                        <item.icon className="h-5 w-5" />
                                        <span className="font-medium">{item.label}</span>
                                        <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
                                    </Link>
                                ))}
                                {/* Settings accessible for surveyors via menu (profile only) */}
                                {!isAdmin() && (
                                    <Link
                                        to="/settings"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                                            isActive('/settings')
                                                ? 'bg-red-50 text-red-600'
                                                : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                        <UserCircle className="h-5 w-5" />
                                        <span className="font-medium">My Account</span>
                                        <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
                                    </Link>
                                )}
                            </nav>
                            
                            <div className="p-4 border-t border-slate-200">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                                    onClick={handleLogout}
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="h-5 w-5 mr-3" />
                                    Sign Out
                                </Button>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </header>

            {/* Desktop Sidebar */}
            <div className="hidden lg:flex">
                <aside className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-200 flex flex-col z-40">
                    <div className="p-4 border-b border-slate-200">
                        <Link to={isAdmin() ? '/dashboard' : '/jobs'} className="flex items-center gap-3">
                            <AppIcon size="md" />
                            <div>
                                <span className="font-heading font-bold text-lg text-slate-900 uppercase tracking-tight block">
                                    {displayName}
                                </span>
                            </div>
                        </Link>
                    </div>
                    
                    <nav className="flex-1 p-3">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                                    isActive(item.path)
                                        ? 'bg-red-50 text-red-600 font-medium'
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                                data-testid={`desktop-nav-${item.label.toLowerCase()}`}
                            >
                                <item.icon className="h-5 w-5" />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                        {/* My Account link for surveyors on desktop */}
                        {!isAdmin() && (
                            <Link
                                to="/settings"
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                                    isActive('/settings')
                                        ? 'bg-red-50 text-red-600 font-medium'
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                <UserCircle className="h-5 w-5" />
                                <span>My Account</span>
                            </Link>
                        )}
                    </nav>
                    
                    <div className="p-3 border-t border-slate-200">
                        <div className="flex items-center gap-3 px-4 py-2 mb-2">
                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">
                                    {user?.name?.charAt(0) || 'U'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate text-sm">{user?.name}</p>
                                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                            onClick={handleLogout}
                            data-testid="desktop-logout-btn"
                        >
                            <LogOut className="h-4 w-4 mr-3" />
                            Sign Out
                        </Button>
                    </div>
                </aside>
            </div>

            {/* Main Content */}
            <main className="flex-1 lg:ml-64">
                <div className="p-4 lg:p-6 pb-24 lg:pb-6">
                    {children}
                </div>
            </main>

            {/* Bottom Navigation - Mobile */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 safe-area-bottom z-40">
                <div className="flex justify-around items-center">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex flex-col items-center py-1 px-3 rounded-lg min-w-[64px] ${
                                isActive(item.path)
                                    ? 'text-red-600'
                                    : 'text-slate-400'
                            }`}
                            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                        >
                            <item.icon className="h-6 w-6" strokeWidth={isActive(item.path) ? 2 : 1.5} />
                            <span className="text-xs mt-1 font-medium">{item.label}</span>
                        </Link>
                    ))}
                </div>
            </nav>
        </div>
    );
};
